import { CurrencyPipe, NgIf } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { combineLatest, lastValueFrom } from 'rxjs';
import { Product, ProductVariant } from 'src/app/classes/utility';
import {
  DeliveryEstimate,
  DeliveryMessagingService,
} from 'src/app/service/delivery-messaging.service';
import { EnvironmentService } from 'src/app/service/environment.service';
import { ShopifyService } from 'src/app/service/shopify.service';
import { ThemeService } from 'src/app/service/theme.service';
import { ProductService } from '../../../product.service';

@Component({
  selector: 'app-add-to-cart',
  imports: [NgIf, RouterModule],
  templateUrl: './add-to-cart.component.html',
  styleUrl: './add-to-cart.component.scss',
})
export class AddToCartComponent implements OnInit {
  @Input() product!: Product;
  _preorderConfig: any;
  _preorders: any = [];
  _selectedVariant!: ProductVariant;
  _text: string = 'Loading';
  _preorderMessaging: string | undefined;
  _purchaseable: 'add' | 'backorder' | 'preorder' | 'outofstock' | 'loading' =
    'loading';
  environment = this.environmentService.environment;
  _selectedSleeve: any;
  _visible: boolean = true;
  _deliveryMessage: DeliveryEstimate | null = null;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private productService: ProductService,
    private shopifyService: ShopifyService,
    private environmentService: EnvironmentService,
    private deliveryMessagingService: DeliveryMessagingService,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    // Get user location for delivery messaging
    this.environmentService.getUserLocation().subscribe((location) => {
      console.log('User location detected:', location);
      if (
        location &&
        this.deliveryMessagingService.isUserInAustralia(location.country)
      ) {
        console.log(
          'User is in Australia, getting delivery message for state:',
          location.region
        );

        // Use the new method that includes countdown in the message
        this.deliveryMessagingService
          .getAustralianDeliveryMessageWithCountdown(location.region)
          .subscribe((deliveryMessage) => {
            this._deliveryMessage = deliveryMessage;
          });
      } else {
        console.log('User is not in Australia or location not available');
      }
    });

    combineLatest([
      this.productService.variantObservable,
      this.productService.sleeveObservable,
    ]).subscribe(async ([product, sleeve]) => {
      this._selectedVariant = product;
      this._selectedSleeve = sleeve;
      this._preorderMessaging = undefined;
      this._purchaseable = 'loading';
      this._text = 'Loading';
      this._visible = !this.product.tags
        .split(', ')
        .find((x) => x == '__HIDDEN');
      await this.fetchPreorders();
      this.calculatePurchaseable();
    });
  }

  async fetchPreorders() {
    await lastValueFrom(
      this.shopifyService.executeQuery(
        `query GetProductVariantsWithSpecificMetafield($productId: ID!, $namespace: String!, $key: String!) {
  product(id: $productId) {
    variants(first: 10) {
      edges {
        node {
          id
          title
          metafield(namespace: $namespace, key: $key) {
            id
            value
          }
        }
      }
    }
  }
}`,
        {
          productId: this.product.admin_graphql_api_id,
          namespace: 'PRP',
          key: 'preorder',
        }
      )
    )
      .then((data) => {
        if (data.data.product) {
          this._preorders = data.data.product.variants.edges.map(
            (x: any) => x.node
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  popDrawer() {
    this.themeService.drawerOpen();
  }

  findPreorderForVariant(variantId: number) {
    const rawData = this._preorders.find((x: any) => x.id.includes(variantId));
    try {
      const preorderConfig = JSON.parse(rawData.metafield.value);
      if (this.isDateInTheFuture(preorderConfig.date)) {
        this._preorderConfig = preorderConfig;
        this._preorderMessaging = preorderConfig.message;
      }
    } catch (err) {
      this._preorderConfig = undefined;
      this._preorderMessaging = undefined;
    }
  }

  isDateInTheFuture(date: string) {
    const compare: Date = new Date(date);
    const now = new Date();
    return compare > now;
  }

  calculateAddToCartMessage() {
    let message =
      this._preorderConfig?.preorder === true
        ? 'Preorder'
        : this._preorderConfig?.preorder === false
        ? 'Backorder'
        : 'Add to Bag';
    this._text =
      message +
      ' - ' +
      new CurrencyPipe(this.locale).transform(
        '' +
          (parseFloat(this._selectedVariant!.price) +
            parseFloat(this._selectedSleeve?.price ?? 0))
      );
  }

  calculatePurchaseable() {
    this.findPreorderForVariant(this._selectedVariant.id);

    if (
      this._selectedVariant.inventory_policy === 'DENY' &&
      this._selectedVariant.inventory_quantity <= 0
    ) {
      this._purchaseable = 'outofstock';
      this._text = 'Out of Stock';
      return;
    }

    this.calculateAddToCartMessage();
    this._purchaseable = 'add';
  }

  addToCart() {
    const originalText = this._text;
    this._text = 'Adding...';
    let attributes: any = [];
    let property =
      this._preorderConfig?.preorder === true
        ? 'Preorder'
        : this._preorderConfig?.preorder === false
        ? 'Backorder'
        : undefined;
    if (property) {
      attributes.push({
        key: property,
        value: this._preorderMessaging,
      });
    }
    let products = [
      {
        variantId: this._selectedVariant.admin_graphql_api_id,
        quantity: 1,
        attributes: attributes,
      },
    ];
    if (this._selectedSleeve) {
      products.push({
        variantId: 'gid://shopify/ProductVariant/' + this._selectedSleeve.id,
        quantity: 1,
        attributes: [],
      });
    }
    this.shopifyService.addToCart(products).subscribe({
      next: (data: any) => {
        this._text = 'Added To Cart!';
        let toasts = [this._selectedVariant.admin_graphql_api_id];
        if (this._selectedSleeve) {
          toasts.push(
            'gid://shopify/ProductVariant/' + this._selectedSleeve.id
          );
        }
        this.themeService.deployToasts(toasts, data);
      },
      error: () => {
        this._text = 'Error Adding to Cart';
      },
      complete: () => {
        setTimeout(() => {
          this._text = originalText;
        }, 3000);
      },
    });
  }
}
